import { Injectable } from '@angular/core';
import { forkJoin, Observable, of } from 'rxjs';
import { catchError, finalize, map, share, switchMap, take, tap } from 'rxjs/operators';
import { AuthenticationService } from './AuthenticationService';
import { BasicResponse } from '../responses/basicResponse';
import { User } from '../models/user';
import { FileDefinitionApiService } from './FileDefinitionApiService';
import { FileDefinition } from '../models/fileDefinition';
import { FileDefinitionResponse } from '../responses/fileDefinitionResponse';
import { TransformationDefinition } from '../models/transformationDefinition';
import { TransformationDefinitionResponse } from '../responses/transformationDefinitionResponse';
import { TransformationRuleDefinition } from '../models/transformationRuleDefinition';
import { TransformationRuleDefinitionResponse } from '../responses/transformationRuleDefinitionResponse';
import { FilterDefinition } from '../models/filterDefinition';
import { FilterRuleDefinition } from '../models/filterRuleDefinition';
import { FilterRuleDefinitionResponse } from '../responses/filterRuleDefinitionResponse';
import { FilterDefinitionResponse } from '../responses/filterDefinitionResponse';
import { Router } from '@angular/router';
import { MetaDataRuleDefinition } from '../models/metaDataRuleDefinition';
import { MetaDataRuleDefinitionResponse } from '../responses/metaDataRuleDefinitionResponse';
import { Customer } from '../models/customer';
import { ConsolidationRuleDefinition } from '../models/consolidationRuleDefinition';
import { ConsolidationDefinition } from '../models/consolidationDefinition';
import { ConsolidationRuleDefinitionResponse } from '../responses/consolidationRuleDefinitionResponse';
import { ConsolidationDefinitionResponse } from '../responses/consolidationDefinitionResponse';
import { ScopeRestriction } from '../models/scopeRestriction';
import { ScopeRestrictionResponse } from '../responses/scopeRestrictionResponse';
import { TransformationDefinitionDTO } from '../DTO/transformationDefinitionDTO';
import { FileDefinitionDTO } from '../DTO/fileDefinitionDTO';
import { FilterDefinitionDTO } from '../DTO/filterDefinitionDTO';
import { ConsolidationDefinitionDTO } from '../DTO/consolidationDefinitionDTO';
import { ValidationRuleDefinition } from '../models/validationRuleDefinition';
import { ValidationDefinition } from '../models/validationDefinition';
import { ValidationRuleDefinitionResponse } from '../responses/validationRuleDefinitionResponse';
import { ValidationDefinitionResponse } from '../responses/validationDefinitionResponse';
import { ValidationDefinitionDTO } from '../DTO/validationDefinitionDTO';
import { AllocationDefinition } from '../models/allocationDefinition';
import { AllocationDefinitionResponse } from '../responses/allocationDefinitionResponse';
import { AllocationDefinitionDTO } from '../DTO/allocationDefinitionDTO';
import { MoovStep } from '../models/moovStep';


@Injectable({ providedIn: 'root' })
export class FileDefinitionService {
    private fileDefinitionsObservableCache?: Observable<FileDefinition[]>;
    private fileDefinitionsCache?: FileDefinition[];

    private scopeRestrictionsObservableCache?: Observable<ScopeRestriction[]>;
    private scopeRestrictionsCache?: ScopeRestriction[];

    private transformationRuleDefinitionsObservableCache?: Observable<TransformationRuleDefinition[]>;
    private transformationRuleDefinitionsCache?: TransformationRuleDefinition[];

    private transformationDefinitionsObservableCache?: Observable<TransformationDefinition[]>;
    private transformationDefinitionsCache?: TransformationDefinition[];

    private transformationDefinitionByIdObservableCache: {[transformationDefinitionId:number]: Observable<TransformationDefinition | undefined>}= {};
    private transformationDefinitionByIdCache: {[transformationDefinitionId:number]: TransformationDefinition} = {}; 

    private filterRuleDefinitionsObservableCache?: Observable<FilterRuleDefinition[]>;
    private filterRuleDefinitionsCache?: FilterRuleDefinition[];

    private filterDefinitionsObservableCache?: Observable<FilterDefinition[]>;
    private filterDefinitionsCache?: FilterDefinition[];

    private filterDefinitionByIdObservableCache: {[filterDefinitionId:number]: Observable<FilterDefinition | undefined>}= {};
    private filterDefinitionByIdCache: {[filterDefinitionId:number]: FilterDefinition} = {};

    private validationRuleDefinitionsObservableCache?: Observable<ValidationRuleDefinition[]>;
    private validationRuleDefinitionsCache?: ValidationRuleDefinition[];

    private validationDefinitionsObservableCache?: Observable<ValidationDefinition[]>;
    private validationDefinitionsCache?: ValidationDefinition[];

    private validationDefinitionByIdObservableCache: {[validationDefinitionId:number]: Observable<ValidationDefinition | undefined>}= {};
    private validationDefinitionByIdCache: {[validationDefinitionId:number]: ValidationDefinition} = {};

    private consolidationRuleDefinitionsObservableCache?: Observable<ConsolidationRuleDefinition[]>;
    private consolidationRuleDefinitionsCache?: ConsolidationRuleDefinition[];

    private consolidationDefinitionsObservableCache?: Observable<ConsolidationDefinition[]>;
    private consolidationDefinitionsCache?: ConsolidationDefinition[];

    private consolidationDefinitionByIdObservableCache: {[consolidationDefinitionId:number]: Observable<ConsolidationDefinition | undefined>}= {};
    private consolidationDefinitionByIdCache: {[consolidationDefinitionId:number]: ConsolidationDefinition} = {};

    private allocationDefinitionsObservableCache?: Observable<AllocationDefinition[]>;
    private allocationDefinitionsCache?: AllocationDefinition[];

    private allocationDefinitionByIdObservableCache: {[allocationDefinitionId:number]: Observable<AllocationDefinition | undefined>}= {};
    private allocationDefinitionByIdCache: {[allocationDefinitionId:number]: AllocationDefinition} = {};

    private metaDataRuleDefinitionsObservableCache?: Observable<MetaDataRuleDefinition[]>;
    private metaDataRuleDefinitionsCache?: MetaDataRuleDefinition[];

    constructor(private fdApiService:FileDefinitionApiService, private authService:AuthenticationService, private router: Router){
        this.authService.currentUserSubject.subscribe(au=>this.processCurrentUser(au));
        this.authService.currentCustomerContextSubject.subscribe(customer=>this.processCurrentCustomerContext(customer))
    }
  
    get authorized(){
        return this.authService.authorizedSubject.value;
    }

    get currentUser(){
        return this.authService.currentUserValue;
    }

    private processCurrentUser(u:User|undefined){
        if(!u){
            //user logged out, clear all caches
            this.clearCache();
        }
    }

    private processCurrentCustomerContext(customer:Customer|undefined){
        this.clearCache();
    }

    private clearCache(){
        this.fileDefinitionsCache=undefined;
        this.scopeRestrictionsCache=undefined;
        this.filterDefinitionsCache=undefined;
        this.filterDefinitionByIdCache={};
        this.validationDefinitionsCache=undefined;
        this.validationDefinitionByIdCache={};
        this.transformationRuleDefinitionsCache=undefined;
        this.transformationDefinitionsCache=undefined;
        this.transformationDefinitionByIdCache={};
        this.consolidationDefinitionsCache=undefined;
        this.consolidationDefinitionByIdCache={};
        this.allocationDefinitionsCache=undefined;
        this.allocationDefinitionByIdCache={};
    }

    public sampleFile(file:File, fileType:string, fieldDelimiter:string, hasHeader:boolean){
        return this.fdApiService.sampleFile(file,fileType, fieldDelimiter, hasHeader);
    }

    public transformFile(file:File, transformationDefinitionId:number){
        return this.fdApiService.transformFile(file, transformationDefinitionId);
    }

    //#region file definitions handling
    public getFileDefinitions(forceReload:boolean){
        if(forceReload) this.fileDefinitionsCache=undefined;
        if(!this.authorized) return of([]);
        // Data available
        if (this.fileDefinitionsCache) return of(this.fileDefinitionsCache);
        // Request pending
        else if (this.fileDefinitionsObservableCache) return this.fileDefinitionsObservableCache;
        // New request needed
        else this.fileDefinitionsObservableCache = this.fetchFileDefinitions();

        return this.fileDefinitionsObservableCache;
    }

    private fetchFileDefinitions(){
        return this.fdApiService.getFileDefinitions(this.authService.entityContextId,this.authService.entityContextType).pipe(map((r)=>this.processFileDefinitions(r)), share());
    }

    private processFileDefinitions(r:FileDefinitionResponse){
        this.fileDefinitionsCache=new Array<FileDefinition>(0);
        if(r.success){
            r.fileDefinitions?.forEach(dto=>{
                var p = this.processFileDefinitionDTO(dto);
                this.fileDefinitionsCache!.push(p);
            });
        }
        else{
            this.handleError(typeof(r), r.message);
        }
        this.fileDefinitionsObservableCache=undefined;
        return this.fileDefinitionsCache;
    }

    private processFileDefinitionDTO(dto:FileDefinitionDTO, obj?:FileDefinition):FileDefinition{
        return FileDefinition.mapFromDTO(dto, obj);
    }

    public getFileDefinitionById(forceReload:boolean, id:number){
        return this.getFileDefinitions(forceReload).pipe(map(fdArray=>{
            const fd = fdArray.find(fd=>fd.id===id);
            return fd!;
        }));
    }

    public createFileDefinition(fd:FileDefinition){
        return this.fdApiService.createFileDefinition(this.authService.currentUserValue!.id, this.authService.entityContextId,this.authService.entityContextType, fd).pipe(map((r:FileDefinitionResponse)=>{
            if(r.success) this.processCreateFileDefinition(r);
            return r;
        }), share());
    }
    private processCreateFileDefinition(r:FileDefinitionResponse){
        if(r.fileDefinitions && r.fileDefinitions.length>0){
            var f = this.processFileDefinitionDTO(r.fileDefinitions[0]);
            this.fileDefinitionsCache?.push(f);
        }
        else{
            this.handleError(typeof(r), r.message);
        }
    }

    public updateFileDefinition(fd:FileDefinition,indexTracker:any[]){
        return this.fdApiService.updateFileDefinition(this.authService.currentUserValue!.id, this.authService.entityContextId,this.authService.entityContextType, fd,indexTracker).pipe(map((r:FileDefinitionResponse)=>{
            if(r.success) this.processUpdateFileDefinition(r);
            return r;
        }), share());
    }
    private processUpdateFileDefinition(r:FileDefinitionResponse){
        if(r.fileDefinitions && r.fileDefinitions.length>0){
            let dto=r.fileDefinitions[0];
            let index = this.fileDefinitionsCache?.findIndex(fd=>fd.id==dto.id);
            if(index && index>-1){
                //map into same object to preserve references from other objects.
                this.processFileDefinitionDTO(dto,this.fileDefinitionsCache![index]);
            }
            else{
                this.fileDefinitionsCache?.push(this.processFileDefinitionDTO(dto));
            }
        }
        else{
            this.handleError(typeof(r), r.message);
        }
    }

    public deleteFileDefinition(fd:FileDefinition){
        return this.fdApiService.deleteFileDefinition(this.authService.currentUserValue!.id, this.authService.entityContextId,this.authService.entityContextType, fd.id!).pipe(map((r:BasicResponse)=>{
            if(r.success) this.processDeleteFileDefinition(fd);
            return r;
        }), share());
    }
    private processDeleteFileDefinition(fd:FileDefinition){
        fd.deleted=true;
    }

    public undeleteFileDefinition(fd:FileDefinition){
        return this.fdApiService.undeleteFileDefinition(this.authService.currentUserValue!.id, this.authService.entityContextId,this.authService.entityContextType, fd.id!).pipe(map((r:BasicResponse)=>{
            if(r.success) this.processUndeleteFileDefinition(fd);
            return r;
        }), share());
    }
    private processUndeleteFileDefinition(fd:FileDefinition){
        fd.deleted=false;
    }

    public publishFileDefinition(fd:FileDefinition, published:boolean){
        return this.fdApiService.publishFileDefinition(this.authService.currentUserValue!.id, this.authService.entityContextId,this.authService.entityContextType, fd.id!, published).pipe(map((r:BasicResponse)=>{
            return this.processPublishFileDefinition(r, fd, published);
        }), share());
    }
    
    private processPublishFileDefinition(r:BasicResponse, fd:FileDefinition, published:boolean){
        if(r.success) fd.published=published;
        return r;
    }

    public getScopeRestrictions(forceReload:boolean){
        if(forceReload) this.scopeRestrictionsCache=undefined;
        if(!this.authorized) of([]);
        // Data available
        if (this.scopeRestrictionsCache) return of(this.scopeRestrictionsCache);
        // Request pending
        else if (this.scopeRestrictionsObservableCache) return this.scopeRestrictionsObservableCache;
        // New request needed
        else this.scopeRestrictionsObservableCache = this.fetchScopeRestrictions();

        return this.scopeRestrictionsObservableCache;
    }

    private fetchScopeRestrictions(){
        return this.fdApiService.getScopeRestrictions().pipe(map((r)=>this.processScopeRestrictions(r)), share());
    }

    private processScopeRestrictions(r:ScopeRestrictionResponse){
        this.scopeRestrictionsObservableCache=undefined;
        this.scopeRestrictionsCache=new Array<ScopeRestriction>(0);
        if(r.success){
            r.scopeRestrictions?.forEach(dto=>{
                var sr = ScopeRestriction.mapFromDTO(dto)
                this.scopeRestrictionsCache!.push(sr);
            });
        }
        else{
            this.handleError(typeof(r), r.message);
        }
        return this.scopeRestrictionsCache;
    }

    public getFileDefinitionDependants(fileDefinitionId:number){
        return this.fdApiService.getFileDefinitionDependants(fileDefinitionId, this.authService.entityContextId,this.authService.entityContextType);
    }

    //resolves the file definition describing the data a moov step operates on:
    //the step's own fileDefinition parameter, else the file definition of the closest preceding format defining step
    //(transformation step: the file definition it conforms with, apply file definition step: its fileDefinition parameter), else the trigger step's fileDefinition parameter
    //includeOwnParameter=false if the caller tracks the step's own fileDefinition parameter itself (e.g. while it is being edited)
    public resolveFileDefinitionForStep(moovSteps:MoovStep[], moovStep:MoovStep, includeOwnParameter:boolean=true):Observable<FileDefinition|undefined>{
        return this.resolveFileDefinitionIdForStep(moovSteps, moovStep, includeOwnParameter).pipe(switchMap(id=>
            id ? this.getFileDefinitionById(false, id).pipe(take(1)) : of(undefined)));
    }

    //same resolution as resolveFileDefinitionForStep, but only the id (no lookup in the loaded file definitions)
    public resolveFileDefinitionIdForStep(moovSteps:MoovStep[], moovStep:MoovStep, includeOwnParameter:boolean=true):Observable<number|undefined>{
        const ownFileDefinitionId = includeOwnParameter ? this.parseIdParameter(moovStep, "fileDefinition") : undefined;
        if(ownFileDefinitionId) return of(ownFileDefinitionId);
        const precedingFormatDefiningStep = this.findPrecedingFormatDefiningStep(moovSteps, moovStep);
        if(precedingFormatDefiningStep?.moovStepDefinition.isApplyFileDefinitionStep){
            return of(this.parseIdParameter(precedingFormatDefiningStep, "fileDefinition"));
        }
        if(precedingFormatDefiningStep){
            const transformationDefinitionId = this.parseIdParameter(precedingFormatDefiningStep, "transformationDefinition");
            if(!transformationDefinitionId) return of(undefined);
            return this.getTransformationDefinitionById(false, transformationDefinitionId).pipe(take(1), map(td=>td?.conformsWithFileDefinitionId || undefined));
        }
        const trigger = moovSteps.length>0 ? moovSteps[0] : undefined;
        return of(trigger ? this.parseIdParameter(trigger, "fileDefinition") : undefined);
    }

    public findPrecedingTransformationStep(moovSteps:MoovStep[], startingMoovStep:MoovStep):MoovStep | undefined{
      return this.findPrecedingStep(moovSteps, startingMoovStep, ms=>ms.moovStepDefinition.isTransformationStep);
    }

    //apply file definition step whose data the starting moov step receives (directly or via subsequent steps); undefined if a reset data step went back to before it
    public findPrecedingApplyFileDefinitionStep(moovSteps:MoovStep[], startingMoovStep:MoovStep):MoovStep | undefined{
      return this.findPrecedingStep(moovSteps, startingMoovStep, ms=>ms.moovStepDefinition.isApplyFileDefinitionStep);
    }

    //transformation or apply file definition step: both (re)define the format of the data passed on to subsequent steps
    public findPrecedingFormatDefiningStep(moovSteps:MoovStep[], startingMoovStep:MoovStep):MoovStep | undefined{
      return this.findPrecedingStep(moovSteps, startingMoovStep, ms=>ms.moovStepDefinition.isTransformationStep || ms.moovStepDefinition.isApplyFileDefinitionStep);
    }

    //(re)calculates input and output formats of all moov steps in sequence, so a changed step passes its format on to all subsequent steps
    public setMoovStepFormats(moovSteps:MoovStep[]){
      [...moovSteps].sort((a,b)=>a.sequenceNo-b.sequenceNo).forEach(ms=>this.setMoovStepFormat(moovSteps, ms));
    }

    //sets input and output format of a moov step based on the formats of its preceding moov steps in moovSteps (fd=file definition, td=transformation definition, doc=document, enc=encrypted)
    public setMoovStepFormat(moovSteps:MoovStep[], ms:MoovStep){
      const msd = ms.moovStepDefinition;
      if(msd.isDataTriggerStep){
        ms.inputFormatType="fd";
        ms.inputFormatId=parseInt((ms.defaultParameters.find(dp=>dp.name=="fileDefinition")?.value ?? ""));
        ms.outputFormatType=ms.inputFormatType;
        ms.outputFormatId=ms.inputFormatId;
        return;
      }
      if(msd.isDocumentTriggerStep){
        ms.inputFormatType="doc";
        ms.inputFormatId=0;
        ms.outputFormatType=ms.inputFormatType;
        ms.outputFormatId=ms.inputFormatId;
        return;
      }
      if(msd.isResetDataStep){
        //data of the step reset to
        const value = ms.defaultParameters.find(dp=>dp.name.toLowerCase()=="previousstep")?.value;
        const resetMoovStep = moovSteps.find(s=>s.sequenceNo==(value?parseInt(value):0));
        if(!resetMoovStep) return;
        ms.inputFormatType=resetMoovStep.outputFormatType;
        ms.inputFormatId=resetMoovStep.outputFormatId;
        ms.outputFormatType=resetMoovStep.outputFormatType;
        ms.outputFormatId=resetMoovStep.outputFormatId;
        return;
      }
      const precedingMoovStep = moovSteps.find(s=>s.sequenceNo==ms.sequenceNo-1);
      if(!precedingMoovStep) return;
      ms.inputFormatType=precedingMoovStep.outputFormatType;
      ms.inputFormatId=precedingMoovStep.outputFormatId;
      if(msd.isTransformationStep){
        ms.outputFormatType="td";
        ms.outputFormatId=parseInt((ms.defaultParameters.find(dp=>dp.name=="transformationDefinition")?.value ?? ""));
      }
      else if(msd.isApplyFileDefinitionStep){
        ms.outputFormatType="fd";
        ms.outputFormatId=parseInt((ms.defaultParameters.find(dp=>dp.name=="fileDefinition")?.value ?? ""));
      }
      else if(msd.stepType==="en"){
        ms.outputFormatType="enc";
        ms.outputFormatId=0;
      }
      else if(msd.stepType==="dc" && ms.inputFormatType=="enc"){
        //decryption restores the format the closest preceding encryption step received; without one (e.g. file received encrypted) the format is passed on
        const encryptionStep = this.findPrecedingStep(moovSteps, ms, s=>s.moovStepDefinition.stepType==="en");
        ms.outputFormatType=encryptionStep ? encryptionStep.inputFormatType : ms.inputFormatType;
        ms.outputFormatId=encryptionStep ? encryptionStep.inputFormatId : ms.inputFormatId;
      }
      else{
        ms.outputFormatType=ms.inputFormatType;
        ms.outputFormatId=ms.inputFormatId;
      }
    }

    private findPrecedingStep(moovSteps:MoovStep[], startingMoovStep:MoovStep, isSearchedStep:(ms:MoovStep)=>boolean):MoovStep | undefined{
      //CAREFUL: recursive method to identify correct preceding searched step if any considering reset data steps (could be multiple).
      let precedingResetDataSteps = moovSteps.filter(ms=>ms.moovStepDefinition.isResetDataStep && ms.sequenceNo<startingMoovStep.sequenceNo);
      let closestResetDataStep = precedingResetDataSteps?.length>0?precedingResetDataSteps.reduce(function(prev, current) { return (prev && prev.sequenceNo > current.sequenceNo) ? prev : current; }):undefined;
      let precedingSearchedSteps = moovSteps.filter(ms=>isSearchedStep(ms) && ms.sequenceNo<startingMoovStep.sequenceNo);
      let closestSearchedStep = precedingSearchedSteps?.length>0?precedingSearchedSteps.reduce(function(prev, current) { return (prev && prev.sequenceNo > current.sequenceNo) ? prev : current; }):undefined;
      //if both above exist, which one is closer to this moov step
      //if the closer one is a reset data step, we need to find the moovStep it resets to and go into recursion
      if(closestResetDataStep && closestSearchedStep && closestResetDataStep.sequenceNo>closestSearchedStep.sequenceNo){
        let value = closestResetDataStep.defaultParameters.find(dp=>dp.name.toLowerCase()=="previousstep")?.value;
        let seqNo = value?parseInt(value):0; //there is always a value in here, so it should always parse
        let resetMoovStep = moovSteps.find(ms => ms.sequenceNo==seqNo);
        if(resetMoovStep){
          if(resetMoovStep.moovStepDefinition.isTriggerStep) closestSearchedStep = undefined;
          else if(isSearchedStep(resetMoovStep)) closestSearchedStep = resetMoovStep;
          else closestSearchedStep = this.findPrecedingStep(moovSteps, resetMoovStep, isSearchedStep);
        }
      }
      return closestSearchedStep;
    }

    private parseIdParameter(moovStep:MoovStep, parameterName:string):number|undefined{
        const value = moovStep.defaultParameters?.find(dp=>dp.name.toLowerCase()==parameterName.toLowerCase())?.value;
        const id = value ? parseInt(value) : NaN;
        return isNaN(id) || id<=0 ? undefined : id;
    }
    //#endregion

    //#region transformation definition handling
    public getTransformationRuleDefinitions(forceReload:boolean){
        if(forceReload) this.transformationRuleDefinitionsCache=undefined;
        if(!this.authorized) return of([]);
        // Data available
        if (this.transformationRuleDefinitionsCache) return of(this.transformationRuleDefinitionsCache);
        // Request pending
        else if (this.transformationRuleDefinitionsObservableCache) return this.transformationRuleDefinitionsObservableCache;
        // New request needed
        else this.transformationRuleDefinitionsObservableCache = this.fetchTransformationRuleDefinitions();

        return this.transformationRuleDefinitionsObservableCache;
    }

    private fetchTransformationRuleDefinitions(){
        return this.fdApiService.getTransformationRuleDefinitions().pipe(map((r)=>this.processTransformationRuleDefinitions(r)), share());
    }

    private processTransformationRuleDefinitions(r:TransformationRuleDefinitionResponse){
        this.transformationRuleDefinitionsObservableCache=undefined;
        this.transformationRuleDefinitionsCache=new Array<TransformationRuleDefinition>(0);
        if(r.success){
            r.transformationRuleDefinitions?.forEach(dto=>{
                var t = TransformationRuleDefinition.mapFromDTO(dto)
                this.transformationRuleDefinitionsCache!.push(t);
            });
        }
        else{
            this.handleError(typeof(r), r.message);
        }
        return this.transformationRuleDefinitionsCache;
    }

    public getTransformationRuleDefinitionById(id:number){
       return this.getTransformationRuleDefinitions(false).pipe(map((r)=>{
            return r?.find(d=>d.id==id);
        }));
    }

    public getTransformationDefinitions(forceReload:boolean){
        if(forceReload) this.transformationDefinitionsCache=undefined;
        if(!this.authorized) return of([]);
        // Data available
        if (this.transformationDefinitionsCache) return of(this.transformationDefinitionsCache);
        // Request pending
        else if (this.transformationDefinitionsObservableCache) return this.transformationDefinitionsObservableCache;
        // New request needed
        else this.transformationDefinitionsObservableCache = this.fetchTransformationDefinitions();

        return this.transformationDefinitionsObservableCache;
    }

    private fetchTransformationDefinitions() {
        return this.fdApiService.getTransformationDefinitions(this.authService.entityContextId, this.authService.entityContextType).pipe(
            switchMap(r => this.processTransformationDefinitions(r)),
            share()
        );
    }

    private processTransformationDefinitions(r: TransformationDefinitionResponse): Observable<TransformationDefinition[]> {
        if (!r.success) {
            this.handleError(typeof r, r.message);
            return of([]);
        }

        const defs = r.transformationDefinitions ?? [];

        const processed$ = defs.map(dto =>
            this.processTransformationDefinitionDTO(dto).pipe(take(1))
        );

        return forkJoin(processed$).pipe(
            tap(result => {
            this.transformationDefinitionsCache = result;
            this.transformationDefinitionsObservableCache = undefined;
            })
        );
    }

    public getTransformationDefinitionById(forceReload:boolean, transformationDefinitionId:number):Observable<TransformationDefinition|undefined>{
        if(forceReload) delete this.transformationDefinitionByIdCache[transformationDefinitionId];
        if(!this.authorized) return of(undefined);
        // Data available
        if (this.transformationDefinitionByIdCache[transformationDefinitionId]) return of(this.transformationDefinitionByIdCache[transformationDefinitionId]);
        // Request pending
        else if (this.transformationDefinitionByIdObservableCache[transformationDefinitionId]) return this.transformationDefinitionByIdObservableCache[transformationDefinitionId];
        // New request needed
        else {
            this.transformationDefinitionByIdObservableCache[transformationDefinitionId] = this.fetchTransformationDefinitionById(transformationDefinitionId);
        }

        return this.transformationDefinitionByIdObservableCache[transformationDefinitionId];
    }

    private fetchTransformationDefinitionById(transformationDefinitionId: number) {
    return this.fdApiService.getTransformationDefinition(this.authService.entityContextId, this.authService.entityContextType, transformationDefinitionId).pipe(
        switchMap(r => this.processTransformationDefinitionById(r, transformationDefinitionId)),
        finalize(() => delete this.transformationDefinitionByIdObservableCache[transformationDefinitionId]),
        share()
    );
}

    private processTransformationDefinitionById(r: TransformationDefinitionResponse, transformationDefinitionId: number) {
        if (!r.success) {
            this.handleError(typeof(r), r.message);
            return of(undefined);
        }
        if (!r.transformationDefinitions?.length) {
            return of(undefined);
        }
        return this.processTransformationDefinitionDTO(r.transformationDefinitions[0]).pipe(
            tap(t => {
                this.transformationDefinitionByIdCache[transformationDefinitionId] = t;
            })
        );
    }

    private processTransformationDefinitionDTO(dto:TransformationDefinitionDTO):Observable<TransformationDefinition>{
        var t = TransformationDefinition.mapFromDTO(dto);
        // Input definition observable
        const input$: Observable<TransformationDefinition | FileDefinition | undefined> = dto.inputDefinitionType === 0 ? this.getFileDefinitionById(false, t.inputDefinitionId) : this.getTransformationDefinitionById(false, t.inputDefinitionId);
        const inputObs$ = input$.pipe(take(1), tap(def => {if(def) t.setInputDefinition(def); }));
        // Rule definition observables
        const ruleObservables = t.transformationProtocols.flatMap(tp =>
            tp.transformationRules.map(tr =>
                this.getTransformationRuleDefinitionById(tr.transformationRuleDefinitionId).pipe(take(1), tap(rd => {
                    tr.transformationRuleDefinition = rd!;
                }))
            )
        );
        // Combine everything
        return forkJoin([inputObs$, ...ruleObservables]).pipe(
            map(() => {
            // Sorting AFTER async work completes
            t.transformationProtocols.forEach(tp => {
                tp.transformationRules.sort((a, b) => a.sequenceNo - b.sequenceNo);
            });

            t.transformationProtocols.sort(
                (a, b) => a.outputFieldIndex - b.outputFieldIndex
            );

            return t;
            })
        );
    }

    public createTransformationDefinition(td: TransformationDefinition) {
        return this.fdApiService.createTransformationDefinition(this.authService.currentUserValue!.id, this.authService.entityContextId, this.authService.entityContextType, td).pipe(switchMap((r: TransformationDefinitionResponse) => {
            if (!r.success) {
                return of(r);
            }
            return this.processCreateTransformationDefinition(r).pipe(
                map(() => r) // return original response after processing completes
            );
        }), share());
    }

    private processCreateTransformationDefinition(r: TransformationDefinitionResponse): Observable<TransformationDefinition | null> {
        if (!r.transformationDefinitions?.length) {
            this.handleError(typeof r, r.message);
            return of(null);
        }

        return this.processTransformationDefinitionDTO(r.transformationDefinitions[0]
        ).pipe(
            tap(t => {
            this.transformationDefinitionsCache?.push(t);
            })
        );
    }

    public updateTransformationDefinition(td: TransformationDefinition) {
    return this.fdApiService.updateTransformationDefinition(this.authService.currentUserValue!.id, this.authService.entityContextId, this.authService.entityContextType, td).pipe(
        switchMap((r: TransformationDefinitionResponse) => {
        if (!r.success) {
            return of(r);
        }

        return this.processUpdateTransformationDefinition(r).pipe(
            map(() => r)
        );
        }), share());
    }
    private processUpdateTransformationDefinition(r: TransformationDefinitionResponse): Observable<TransformationDefinition | null> {
        if (!r.transformationDefinitions?.length) {
            this.handleError(typeof r, r.message);
            return of(null);
        }

        return this.processTransformationDefinitionDTO(r.transformationDefinitions[0]).pipe(tap(t => {
            const cache = this.transformationDefinitionsCache;
            if (cache) {
                const index = cache.findIndex(td => td.id === t.id);

                if (index > -1) {
                cache[index] = t;
                } else {
                cache.push(t);
                }
            }

            if (this.transformationDefinitionByIdCache[t.id!]) {
                this.transformationDefinitionByIdCache[t.id!] = t;
            }
        }));
    }

    public deleteTransformationDefinition(td:TransformationDefinition){
        return this.fdApiService.deleteTransformationDefinition(this.authService.currentUserValue!.id, this.authService.entityContextId,this.authService.entityContextType, td.id!).pipe(map((r:BasicResponse)=>{
            if(r.success) this.processDeleteTransformationDefinition(td);
            return r;
        }), share());
    }
    private processDeleteTransformationDefinition(td:TransformationDefinition){
        td.deleted=true;
    }
    public undeleteTransformationDefinition(td:TransformationDefinition){
        return this.fdApiService.undeleteTransformationDefinition(this.authService.currentUserValue!.id, this.authService.entityContextId,this.authService.entityContextType, td.id!).pipe(map((r:BasicResponse)=>{
            if(r.success) this.processUndeleteTransformationDefinition(td);
            return r;
        }), share());
    }
    private processUndeleteTransformationDefinition(td:TransformationDefinition){
        td.deleted=false;
    }

    public publishTransformationDefinition(td:TransformationDefinition, published:boolean){
        return this.fdApiService.publishTransformationDefinition(this.authService.currentUserValue!.id, this.authService.entityContextId,this.authService.entityContextType, td.id!, published).pipe(map((r:BasicResponse)=>{
            return this.processPublishTransformationDefinition(r, td, published);
        }), share());
    }

    private processPublishTransformationDefinition(r:BasicResponse, td:TransformationDefinition, published:boolean){
        if(r.success) td.published=published;
        return r;
    }

    public previewTransformation(file:File, transformationDefinitionId:number){
        return this.fdApiService.previewTransformation(file, transformationDefinitionId);
    }

    public getTransformationDefinitionDependants(transformationDefinitionId:number){
        return this.fdApiService.getTransformationDefinitionDependants(transformationDefinitionId, this.authService.entityContextId,this.authService.entityContextType);
    }
    //#endregion

    //#region filter definition handling
    public getFilterRuleDefinitions(forceReload:boolean){
        if(forceReload) this.filterRuleDefinitionsCache=undefined;
        if(!this.authorized) return of([]);
        // Data available
        if (this.filterRuleDefinitionsCache) return of(this.filterRuleDefinitionsCache);
        // Request pending
        else if (this.filterRuleDefinitionsObservableCache) return this.filterRuleDefinitionsObservableCache;
        // New request needed
        else this.filterRuleDefinitionsObservableCache = this.fetchFilterRuleDefinitions();

        return this.filterRuleDefinitionsObservableCache;
    }

    private fetchFilterRuleDefinitions(){
        return this.fdApiService.getFilterRuleDefinitions().pipe(map((r)=>this.processFilterRuleDefinitions(r)), share());
    }

    private processFilterRuleDefinitions(r:FilterRuleDefinitionResponse){
        this.filterRuleDefinitionsObservableCache=undefined;
        this.filterRuleDefinitionsCache=new Array<FilterRuleDefinition>(0);
        if(r.success){
            r.filterRuleDefinitions?.forEach(dto=>{
                var f = FilterRuleDefinition.mapFromDTO(dto)
                this.filterRuleDefinitionsCache!.push(f);
            });
        }
        else{
            this.handleError(typeof(r), r.message);
        }
        return this.filterRuleDefinitionsCache;
    }

    public getFilterRuleDefinitionById(id:number){
        return this.getFilterRuleDefinitions(false).pipe(map((r)=>{
            return r.find(d=>d.id===id)!;
        }));
    }

    public getFilterDefinitions(forceReload:boolean){
        if(forceReload) this.filterDefinitionsCache=undefined;
        if(!this.authorized) return of([]);
        // Data available
        if (this.filterDefinitionsCache) return of(this.filterDefinitionsCache);
        // Request pending
        else if (this.filterDefinitionsObservableCache) return this.filterDefinitionsObservableCache;
        // New request needed
        else this.filterDefinitionsObservableCache = this.fetchFilterDefinitions();

        return this.filterDefinitionsObservableCache;
    }

    private fetchFilterDefinitions(){
        return this.fdApiService.getFilterDefinitions( this.authService.entityContextId,this.authService.entityContextType).pipe(
            switchMap((r)=>this.processFilterDefinitions(r)),
            share()
        );
    }

    private processFilterDefinitions(r:FilterDefinitionResponse):Observable<FilterDefinition[]>{
        if(!r.success){
            this.handleError(typeof(r), r.message);
            return of([]);
        }
        const dtos = r.filterDefinitions ?? [];
        if(dtos.length===0){
            this.filterDefinitionsCache=[];
            this.filterDefinitionsObservableCache=undefined;
            return of([]);
        }
        return forkJoin(dtos.map(dto=>this.processFilterDefinitionDTO(dto).pipe(take(1)))).pipe(
            tap(list=>{
                this.filterDefinitionsCache=list;
                this.filterDefinitionsObservableCache=undefined;
            })
        );
    }

    private processFilterDefinitionDTO(dto:FilterDefinitionDTO):Observable<FilterDefinition>{
        const f = FilterDefinition.mapFromDTO(dto);
        const fileDef$ = dto.fileDefinitionId
            ? this.getFileDefinitionById(false, dto.fileDefinitionId).pipe(take(1), tap(fd=>f.setFileDefinition(fd)), catchError(()=>of(undefined)))
            : of(undefined);
        const ruleObservables = f.filterProtocols.flatMap(fp=>
            fp.filterRules.map(fr=>
                this.getFilterRuleDefinitionById(fr.filterRuleDefinitionId).pipe(take(1), tap(frd=>{ fr.filterRuleDefinition=frd; }), catchError(()=>of(undefined)))
            )
        );
        return forkJoin([fileDef$, ...ruleObservables]).pipe(
            map(()=>{
                f.filterProtocols.sort((a,b)=>a.sequenceNo-b.sequenceNo);
                return f;
            })
        );
    }

    public getFilterDefinitionById(forceReload:boolean, filterDefinitionId:number){
        if(forceReload) delete this.filterDefinitionByIdCache[filterDefinitionId];
        if(!this.authorized) return of(undefined)
        // Data available
        if (this.filterDefinitionByIdCache[filterDefinitionId]) return of(this.filterDefinitionByIdCache[filterDefinitionId]);
        // Request pending
        else if (this.filterDefinitionByIdObservableCache[filterDefinitionId]) return this.filterDefinitionByIdObservableCache[filterDefinitionId];
        // New request needed
        else this.filterDefinitionByIdObservableCache[filterDefinitionId] = this.fetchFilterDefinitionById(filterDefinitionId);

        return this.filterDefinitionByIdObservableCache[filterDefinitionId];
    }

    private fetchFilterDefinitionById(filterDefinitionId:number){
        return this.fdApiService.getFilterDefinition(this.authService.entityContextId,this.authService.entityContextType, filterDefinitionId).pipe(
            switchMap((r)=>this.processFilterDefinitionById(r, filterDefinitionId)),
            finalize(()=>delete this.filterDefinitionByIdObservableCache[filterDefinitionId]),
            share()
        );
    }

    private processFilterDefinitionById(r:FilterDefinitionResponse, filterDefinitionId:number):Observable<FilterDefinition|undefined>{
        if(!r.success){
            this.handleError(typeof(r), r.message);
            return of(undefined);
        }
        //only expecting max one definition
        const dto = r.filterDefinitions?.[0];
        if(!dto) return of(undefined);
        return this.processFilterDefinitionDTO(dto).pipe(
            tap(fd=>{ this.filterDefinitionByIdCache[filterDefinitionId]=fd; })
        );
    }

    public createFilterDefinition(fd:FilterDefinition){
        return this.fdApiService.createFilterDefinition(this.authService.currentUserValue!.id, this.authService.entityContextId,this.authService.entityContextType, fd).pipe(
            switchMap((r:FilterDefinitionResponse)=>{
                if(!r.success) return of(r);
                return this.processCreateFilterDefinition(r).pipe(map(()=>r));
            }),
            share()
        );
    }
    private processCreateFilterDefinition(r:FilterDefinitionResponse):Observable<FilterDefinition|null>{
        const dto = r.filterDefinitions?.[0];
        if(!dto){
            this.handleError(typeof(r), r.message);
            return of(null);
        }
        return this.processFilterDefinitionDTO(dto).pipe(
            tap(fd=>{ this.filterDefinitionsCache?.push(fd); })
        );
    }

    public updateFilterDefinition(fd:FilterDefinition){
        return this.fdApiService.updateFilterDefinition(this.authService.currentUserValue!.id, this.authService.entityContextId,this.authService.entityContextType, fd).pipe(
            switchMap((r:FilterDefinitionResponse)=>{
                if(!r.success) return of(r);
                return this.processUpdateFilterDefinition(r).pipe(map(()=>r));
            }),
            share()
        );
    }
    private processUpdateFilterDefinition(r:FilterDefinitionResponse):Observable<FilterDefinition|null>{
        const dto = r.filterDefinitions?.[0];
        if(!dto){
            this.handleError(typeof(r), r.message);
            return of(null);
        }
        return this.processFilterDefinitionDTO(dto).pipe(
            tap(fd=>{
                const cache = this.filterDefinitionsCache;
                if(cache){
                    const index = cache.findIndex(td=>td.id==fd.id);
                    if(index>-1) cache[index]=fd;
                    else cache.push(fd);
                }
                if(fd.id!=null && this.filterDefinitionByIdCache[fd.id]) this.filterDefinitionByIdCache[fd.id]=fd;
            })
        );
    }

    public deleteFilterDefinition(fd:FilterDefinition){
        return this.fdApiService.deleteFilterDefinition(this.authService.currentUserValue!.id, this.authService.entityContextId,this.authService.entityContextType, fd.id!).pipe(map((r:BasicResponse)=>{
            if(r.success) this.processDeleteFilterDefinition(fd);
            return r;
        }), share());
    }
    private processDeleteFilterDefinition(fd:FilterDefinition){
        fd.deleted=true;
    }
    public undeleteFilterDefinition(fd:FilterDefinition){
        return this.fdApiService.undeleteFilterDefinition(this.authService.currentUserValue!.id, this.authService.entityContextId,this.authService.entityContextType, fd.id!).pipe(map((r:BasicResponse)=>{
            if(r.success) this.processUndeleteFilterDefinition(fd);
            return r;
        }), share());
    }
    private processUndeleteFilterDefinition(fd:FilterDefinition){
        fd.deleted=false;
    }

    public publishFilterDefinition(fd:FilterDefinition, published:boolean){
        return this.fdApiService.publishFilterDefinition(this.authService.currentUserValue!.id, this.authService.entityContextId,this.authService.entityContextType, fd.id!, published).pipe(map((r:BasicResponse)=>{
            return this.processPublishFilterDefinition(r, fd, published);
        }), share());
    }

    private processPublishFilterDefinition(r:BasicResponse, fd:FilterDefinition, published:boolean){
        if(r.success) fd.published=published;
        return r;
    }

    public previewFilter(file:File, filterDefinitionId:number){
        return this.fdApiService.previewFilter(file, filterDefinitionId);
    }
    //#endregion
   
    //#region validation definition handling
    public getValidationRuleDefinitions(forceReload:boolean){
        if(forceReload) this.validationRuleDefinitionsCache=undefined;
        if(!this.authorized) return of([]);
        // Data available
        if (this.validationRuleDefinitionsCache) return of(this.validationRuleDefinitionsCache);
        // Request pending
        else if (this.validationRuleDefinitionsObservableCache) return this.validationRuleDefinitionsObservableCache;
        // New request needed
        else this.validationRuleDefinitionsObservableCache = this.fetchValidationRuleDefinitions();

        return this.validationRuleDefinitionsObservableCache;
    }

    private fetchValidationRuleDefinitions(){
        return this.fdApiService.getValidationRuleDefinitions().pipe(map((r)=>this.processValidationRuleDefinitions(r)), share());
    }

    private processValidationRuleDefinitions(r:ValidationRuleDefinitionResponse){
        this.validationRuleDefinitionsObservableCache=undefined;
        this.validationRuleDefinitionsCache=new Array<ValidationRuleDefinition>(0);
        if(r.success){
            r.validationRuleDefinitions?.forEach(dto=>{
                var f = ValidationRuleDefinition.mapFromDTO(dto)
                this.validationRuleDefinitionsCache!.push(f);
            });
        }
        else{
            this.handleError(typeof(r), r.message);
        }
        return this.validationRuleDefinitionsCache;
    }

    public getValidationRuleDefinitionById(id:number){
    return this.getValidationRuleDefinitions(false).pipe(map((r)=>{
            return r!.find(d=>d.id==id);
        }));
    }

    public getValidationDefinitions(forceReload:boolean){
        if(forceReload) this.validationDefinitionsCache=undefined;
        if(!this.authorized) return of([]);
        // Data available
        if (this.validationDefinitionsCache) return of(this.validationDefinitionsCache);
        // Request pending
        else if (this.validationDefinitionsObservableCache) return this.validationDefinitionsObservableCache;
        // New request needed
        else this.validationDefinitionsObservableCache = this.fetchValidationDefinitions();

        return this.validationDefinitionsObservableCache;
    }

    private fetchValidationDefinitions(){
        return this.fdApiService.getValidationDefinitions(this.authService.entityContextId,this.authService.entityContextType).pipe(
            switchMap((r)=>this.processValidationDefinitions(r)),
            share()
        );
    }

    private processValidationDefinitions(r:ValidationDefinitionResponse):Observable<ValidationDefinition[]>{
        if(!r.success){
            this.handleError(typeof(r), r.message);
            return of([]);
        }
        const dtos = r.validationDefinitions ?? [];
        if(dtos.length===0){
            this.validationDefinitionsCache=[];
            this.validationDefinitionsObservableCache=undefined;
            return of([]);
        }
        return forkJoin(dtos.map(dto=>this.processValidationDefinitionDTO(dto).pipe(take(1)))).pipe(
            tap(list=>{
                this.validationDefinitionsCache=list;
                this.validationDefinitionsObservableCache=undefined;
            })
        );
    }

    private processValidationDefinitionDTO(dto:ValidationDefinitionDTO):Observable<ValidationDefinition>{
        const f = ValidationDefinition.mapFromDTO(dto);
        const fileDef$ = dto.fileDefinitionId
            ? this.getFileDefinitionById(false, dto.fileDefinitionId).pipe(take(1), tap(fd=>f.setFileDefinition(fd)), catchError(()=>of(undefined)))
            : of(undefined);
        const ruleObservables = f.validationProtocols.flatMap(fp=>
            fp.validationRules.map(fr=>
                this.getValidationRuleDefinitionById(fr.validationRuleDefinitionId).pipe(take(1), tap(rd=>{ fr.validationRuleDefinition=rd!; }), catchError(()=>of(undefined)))
            )
        );
        return forkJoin([fileDef$, ...ruleObservables]).pipe(
            map(()=>{
                f.validationProtocols.sort((a,b)=>a.sequenceNo-b.sequenceNo);
                return f;
            })
        );
    }

    public getValidationDefinitionById(forceReload:boolean, validationDefinitionId:number){
        if(forceReload) delete this.validationDefinitionByIdCache[validationDefinitionId];
        if(!this.authorized) return of(undefined);
        // Data available
        if (this.validationDefinitionByIdCache[validationDefinitionId]) return of(this.validationDefinitionByIdCache[validationDefinitionId]);
        // Request pending
        else if (this.validationDefinitionByIdObservableCache[validationDefinitionId]) return this.validationDefinitionByIdObservableCache[validationDefinitionId];
        // New request needed
        else this.validationDefinitionByIdObservableCache[validationDefinitionId] = this.fetchValidationDefinitionById(validationDefinitionId);

        return this.validationDefinitionByIdObservableCache[validationDefinitionId];
    }

    private fetchValidationDefinitionById(validationDefinitionId:number){
        return this.fdApiService.getValidationDefinition(this.authService.entityContextId,this.authService.entityContextType, validationDefinitionId).pipe(
            switchMap((r)=>this.processValidationDefinitionById(r, validationDefinitionId)),
            finalize(()=>delete this.validationDefinitionByIdObservableCache[validationDefinitionId]),
            share()
        );
    }

    private processValidationDefinitionById(r:ValidationDefinitionResponse, validationDefinitionId:number):Observable<ValidationDefinition|undefined>{
        if(!r.success){
            this.handleError(typeof(r), r.message);
            return of(undefined);
        }
        //only expecting max one definition
        const dto = r.validationDefinitions?.[0];
        if(!dto) return of(undefined);
        return this.processValidationDefinitionDTO(dto).pipe(
            tap(vd=>{ this.validationDefinitionByIdCache[validationDefinitionId]=vd; })
        );
    }

    public createValidationDefinition(vd:ValidationDefinition){
        return this.fdApiService.createValidationDefinition(this.authService.currentUserValue!.id, this.authService.entityContextId,this.authService.entityContextType, vd).pipe(
            switchMap((r:ValidationDefinitionResponse)=>{
                if(!r.success) return of(r);
                return this.processCreateValidationDefinition(r).pipe(map(()=>r));
            }),
            share()
        );
    }
    private processCreateValidationDefinition(r:ValidationDefinitionResponse):Observable<ValidationDefinition|null>{
        const dto = r.validationDefinitions?.[0];
        if(!dto){
            this.handleError(typeof(r), r.message);
            return of(null);
        }
        return this.processValidationDefinitionDTO(dto).pipe(
            tap(vd=>{ this.validationDefinitionsCache?.push(vd); })
        );
    }

    public updateValidationDefinition(vd:ValidationDefinition){
        return this.fdApiService.updateValidationDefinition(this.authService.currentUserValue!.id, this.authService.entityContextId, this.authService.entityContextType, vd).pipe(
            switchMap((r:ValidationDefinitionResponse)=>{
                if(!r.success) return of(r);
                return this.processUpdateValidationDefinition(r).pipe(map(()=>r));
            }),
            share()
        );
    }
    private processUpdateValidationDefinition(r:ValidationDefinitionResponse):Observable<ValidationDefinition|null>{
        const dto = r.validationDefinitions?.[0];
        if(!dto){
            this.handleError(typeof(r), r.message);
            return of(null);
        }
        return this.processValidationDefinitionDTO(dto).pipe(
            tap(t=>{
                const cache = this.validationDefinitionsCache;
                if(cache){
                    const index = cache.findIndex(td=>td.id==t.id);
                    if(index>-1) cache[index]=t;
                    else cache.push(t);
                }
                this.validationDefinitionByIdCache[t.id]=t;
            })
        );
    }

    public deleteValidationDefinition(vd:ValidationDefinition){
        return this.fdApiService.deleteValidationDefinition(this.authService.currentUserValue!.id, this.authService.entityContextId,this.authService.entityContextType, vd.id).pipe(map((r:BasicResponse)=>{
            if(r.success) this.processDeleteValidationDefinition(vd);
            return r;
        }), share());
    }
    private processDeleteValidationDefinition(vd:ValidationDefinition){
        vd.deleted=true;
    }
    public undeleteValidationDefinition(vd:ValidationDefinition){
        return this.fdApiService.undeleteValidationDefinition(this.authService.currentUserValue!.id, this.authService.entityContextId,this.authService.entityContextType, vd.id).pipe(map((r:BasicResponse)=>{
            if(r.success) this.processUndeleteValidationDefinition(vd);
            return r;
        }), share());
    }
    private processUndeleteValidationDefinition(vd:ValidationDefinition){
        vd.deleted=false;
    }

    public publishValidationDefinition(vd:ValidationDefinition, published:boolean){
        return this.fdApiService.publishValidationDefinition(this.authService.currentUserValue!.id, this.authService.entityContextId,this.authService.entityContextType, vd.id, published).pipe(map((r:BasicResponse)=>{
            return this.processPublishValidationDefinition(r, vd, published);
        }), share());
    }

    private processPublishValidationDefinition(r:BasicResponse, vd:ValidationDefinition, published:boolean){
        if(r.success) vd.published=published;
        return r;
    }

    public previewValidation(file:File, validationDefinitionId:number){
        return this.fdApiService.previewValidation(file, validationDefinitionId);
    }
    //#endregion

    //#region consolidation definition handling
    public getConsolidationRuleDefinitions(forceReload:boolean){
        if(forceReload) this.consolidationRuleDefinitionsCache=undefined;
        if(!this.authorized) return of([]);
        // Data available
        if (this.consolidationRuleDefinitionsCache) return of(this.consolidationRuleDefinitionsCache);
        // Request pending
        else if (this.consolidationRuleDefinitionsObservableCache) return this.consolidationRuleDefinitionsObservableCache;
        // New request needed
        else this.consolidationRuleDefinitionsObservableCache = this.fetchConsolidationRuleDefinitions();

        return this.consolidationRuleDefinitionsObservableCache;
    }

    private fetchConsolidationRuleDefinitions(){
        return this.fdApiService.getConsolidationRuleDefinitions().pipe(map((r)=>this.processConsolidationRuleDefinitions(r)), share());
    }

    private processConsolidationRuleDefinitions(r:ConsolidationRuleDefinitionResponse){
        this.consolidationRuleDefinitionsObservableCache=undefined;
        this.consolidationRuleDefinitionsCache=new Array<ConsolidationRuleDefinition>(0);
        if(r.success){
            r.consolidationRuleDefinitions?.forEach(dto=>{
                var c = ConsolidationRuleDefinition.mapFromDTO(dto)
                this.consolidationRuleDefinitionsCache!.push(c);
            });
        }
        else{
            this.handleError(typeof(r), r.message);
        }
        return this.consolidationRuleDefinitionsCache;
    }

    public getConsolidationRuleDefinitionById(id:number){
       return this.getConsolidationRuleDefinitions(false).pipe(map((r)=>{
            return r.find(d=>d.id==id);
        }));
    }

    public getConsolidationDefinitions(forceReload:boolean){
        if(forceReload) this.consolidationDefinitionsCache=undefined;
        if(!this.authorized) return of([]);
        // Data available
        if (this.consolidationDefinitionsCache) return of(this.consolidationDefinitionsCache);
        // Request pending
        else if (this.consolidationDefinitionsObservableCache) return this.consolidationDefinitionsObservableCache;
        // New request needed
        else this.consolidationDefinitionsObservableCache = this.fetchConsolidationDefinitions();

        return this.consolidationDefinitionsObservableCache;
    }

    private fetchConsolidationDefinitions(){
        return this.fdApiService.getConsolidationDefinitions(this.authService.entityContextId,this.authService.entityContextType).pipe(
            switchMap((r)=>this.processConsolidationDefinitions(r)),
            share()
        );
    }

    private processConsolidationDefinitions(r:ConsolidationDefinitionResponse):Observable<ConsolidationDefinition[]>{
        if(!r.success){
            this.handleError(typeof(r), r.message);
            return of([]);
        }
        const dtos = r.consolidationDefinitions ?? [];
        if(dtos.length===0){
            this.consolidationDefinitionsCache=[];
            this.consolidationDefinitionsObservableCache=undefined;
            return of([]);
        }
        return forkJoin(dtos.map(dto=>this.processConsolidationDefinitionDTO(dto).pipe(take(1)))).pipe(
            tap(list=>{
                this.consolidationDefinitionsCache=list;
                this.consolidationDefinitionsObservableCache=undefined;
            })
        );
    }

    private processConsolidationDefinitionDTO(dto:ConsolidationDefinitionDTO):Observable<ConsolidationDefinition>{
        const c = ConsolidationDefinition.mapFromDTO(dto);
        const fileDef$ = dto.fileDefinitionId
            ? this.getFileDefinitionById(false, dto.fileDefinitionId).pipe(take(1), tap(fd=>c.setFileDefinition(fd)), catchError(()=>of(undefined)))
            : of(undefined);
        const ruleObservables = c.consolidationRules.map(cr=>
            this.getConsolidationRuleDefinitionById(cr.consolidationRuleDefinitionId).pipe(take(1), tap(crd=>{ cr.consolidationRuleDefinition=crd!; }), catchError(()=>of(undefined)))
        );
        return forkJoin([fileDef$, ...ruleObservables]).pipe(map(()=>c));
    }

    public getConsolidationDefinitionById(forceReload:boolean, consolidationDefinitionId:number){
        if(forceReload) delete this.consolidationDefinitionByIdCache[consolidationDefinitionId];
        if(!this.authorized) return of(undefined);
        // Data available
        if (this.consolidationDefinitionByIdCache[consolidationDefinitionId]) return of(this.consolidationDefinitionByIdCache[consolidationDefinitionId]);
        // Request pending
        else if (this.consolidationDefinitionByIdObservableCache[consolidationDefinitionId]) return this.consolidationDefinitionByIdObservableCache[consolidationDefinitionId];
        // New request needed
        else this.consolidationDefinitionByIdObservableCache[consolidationDefinitionId] = this.fetchConsolidationDefinitionById(consolidationDefinitionId);

        return this.consolidationDefinitionByIdObservableCache[consolidationDefinitionId];
    }

    private fetchConsolidationDefinitionById(consolidationDefinitionId:number){
        return this.fdApiService.getConsolidationDefinition( this.authService.entityContextId,this.authService.entityContextType, consolidationDefinitionId).pipe(
            switchMap((r)=>this.processConsolidationDefinitionById(r, consolidationDefinitionId)),
            finalize(()=>delete this.consolidationDefinitionByIdObservableCache[consolidationDefinitionId]),
            share()
        );
    }

    private processConsolidationDefinitionById(r:ConsolidationDefinitionResponse, consolidationDefinitionId:number):Observable<ConsolidationDefinition|undefined>{
        if(!r.success){
            this.handleError(typeof(r), r.message);
            return of(undefined);
        }
        //only expecting max one definition
        const dto = r.consolidationDefinitions?.[0];
        if(!dto) return of(undefined);
        return this.processConsolidationDefinitionDTO(dto).pipe(
            tap(cd=>{ this.consolidationDefinitionByIdCache[consolidationDefinitionId]=cd; })
        );
    }

    public createConsolidationDefinition(cd:ConsolidationDefinition){
        return this.fdApiService.createConsolidationDefinition(this.authService.currentUserValue!.id, this.authService.entityContextId,this.authService.entityContextType, cd).pipe(
            switchMap((r:ConsolidationDefinitionResponse)=>{
                if(!r.success) return of(r);
                return this.processCreateConsolidationDefinition(r).pipe(map(()=>r));
            }),
            share()
        );
    }
    private processCreateConsolidationDefinition(r:ConsolidationDefinitionResponse):Observable<ConsolidationDefinition|null>{
        const dto = r.consolidationDefinitions?.[0];
        if(!dto){
            this.handleError(typeof(r), r.message);
            return of(null);
        }
        return this.processConsolidationDefinitionDTO(dto).pipe(
            tap(cd=>{ this.consolidationDefinitionsCache?.push(cd); })
        );
    }

    public updateConsolidationDefinition(cd:ConsolidationDefinition){
        return this.fdApiService.updateConsolidationDefinition(this.authService.currentUserValue!.id, this.authService.entityContextId,this.authService.entityContextType, cd).pipe(
            switchMap((r:ConsolidationDefinitionResponse)=>{
                if(!r.success) return of(r);
                return this.processUpdateConsolidationDefinition(r).pipe(map(()=>r));
            }),
            share()
        );
    }
    private processUpdateConsolidationDefinition(r:ConsolidationDefinitionResponse):Observable<ConsolidationDefinition|null>{
        const dto = r.consolidationDefinitions?.[0];
        if(!dto){
            this.handleError(typeof(r), r.message);
            return of(null);
        }
        return this.processConsolidationDefinitionDTO(dto).pipe(
            tap(c=>{
                const cache = this.consolidationDefinitionsCache;
                if(cache){
                    const index = cache.findIndex(cd=>cd.id==c.id);
                    if(index>-1) cache[index]=c;
                    else cache.push(c);
                }
                this.consolidationDefinitionByIdCache[c.id]=c;
            })
        );
    }

    public deleteConsolidationDefinition(cd:ConsolidationDefinition){
        return this.fdApiService.deleteConsolidationDefinition(this.authService.currentUserValue!.id, this.authService.entityContextId,this.authService.entityContextType, cd.id).pipe(map((r:BasicResponse)=>{
            if(r.success) this.processDeleteConsolidationDefinition(cd);
            return r;
        }), share());
    }
    private processDeleteConsolidationDefinition(cd:ConsolidationDefinition){
        cd.deleted=true;
    }
    public undeleteConsolidationDefinition(cd:ConsolidationDefinition){
        return this.fdApiService.undeleteConsolidationDefinition(this.authService.currentUserValue!.id, this.authService.entityContextId,this.authService.entityContextType, cd.id).pipe(map((r:BasicResponse)=>{
            if(r.success) this.processUndeleteConsolidationDefinition(cd);
            return r;
        }), share());
    }
    private processUndeleteConsolidationDefinition(cd:ConsolidationDefinition){
        cd.deleted=false;
    }

    public publishConsolidationDefinition(cd:ConsolidationDefinition, published:boolean){
        return this.fdApiService.publishConsolidationDefinition(this.authService.currentUserValue!.id, this.authService.entityContextId,this.authService.entityContextType, cd.id, published).pipe(map((r:BasicResponse)=>{
            return this.processPublishConsolidationDefinition(r, cd, published);
        }), share());
    }

    private processPublishConsolidationDefinition(r:BasicResponse, cd:ConsolidationDefinition, published:boolean){
        if(r.success) cd.published=published;
        return r;
    }

    public previewConsolidation(file:File, consolidationDefinitionId:number){
        return this.fdApiService.previewConsolidation(file, consolidationDefinitionId);
    }
    //#endregion

    //#region allocation definition handling
    public getAllocationDefinitions(forceReload:boolean){
        if(forceReload) this.allocationDefinitionsCache=undefined;
        if(!this.authorized) return of([]);
        // Data available
        if (this.allocationDefinitionsCache) return of(this.allocationDefinitionsCache);
        // Request pending
        else if (this.allocationDefinitionsObservableCache) return this.allocationDefinitionsObservableCache;
        // New request needed
        else this.allocationDefinitionsObservableCache = this.fetchAllocationDefinitions();

        return this.allocationDefinitionsObservableCache;
    }

    private fetchAllocationDefinitions(){
        return this.fdApiService.getAllocationDefinitions(this.authService.entityContextId,this.authService.entityContextType).pipe(
            switchMap((r)=>this.processAllocationDefinitions(r)),
            share()
        );
    }

    private processAllocationDefinitions(r:AllocationDefinitionResponse):Observable<AllocationDefinition[]>{
        if(!r.success){
            this.handleError(typeof(r), r.message);
            return of([]);
        }
        const dtos = r.allocationDefinitions ?? [];
        if(dtos.length===0){
            this.allocationDefinitionsCache=[];
            this.allocationDefinitionsObservableCache=undefined;
            return of([]);
        }
        return forkJoin(dtos.map(dto=>this.processAllocationDefinitionDTO(dto).pipe(take(1)))).pipe(
            tap(list=>{
                this.allocationDefinitionsCache=list;
                this.allocationDefinitionsObservableCache=undefined;
            })
        );
    }

    private processAllocationDefinitionDTO(dto:AllocationDefinitionDTO):Observable<AllocationDefinition>{
        const a = AllocationDefinition.mapFromDTO(dto);
        if(!dto.fileDefinitionId) return of(a);
        return this.getFileDefinitionById(false, dto.fileDefinitionId).pipe(
            take(1),
            map(fd=>{ a.setFileDefinition(fd); return a; }),
            catchError(()=>of(a))
        );
    }

    public getAllocationDefinitionById(forceReload:boolean, allocationDefinitionId:number){
        if(forceReload) delete this.allocationDefinitionByIdCache[allocationDefinitionId];
        if(!this.authorized) return of(undefined);
        // Data available
        if (this.allocationDefinitionByIdCache[allocationDefinitionId]) return of(this.allocationDefinitionByIdCache[allocationDefinitionId]);
        // Request pending
        else if (this.allocationDefinitionByIdObservableCache[allocationDefinitionId]) return this.allocationDefinitionByIdObservableCache[allocationDefinitionId];
        // New request needed
        else this.allocationDefinitionByIdObservableCache[allocationDefinitionId] = this.fetchAllocationDefinitionById(allocationDefinitionId);

        return this.allocationDefinitionByIdObservableCache[allocationDefinitionId];
    }

    private fetchAllocationDefinitionById(allocationDefinitionId:number){
        return this.fdApiService.getAllocationDefinition(this.authService.entityContextId,this.authService.entityContextType, allocationDefinitionId).pipe(
            switchMap((r)=>this.processAllocationDefinitionById(r, allocationDefinitionId)),
            finalize(()=>delete this.allocationDefinitionByIdObservableCache[allocationDefinitionId]),
            share()
        );
    }

    private processAllocationDefinitionById(r:AllocationDefinitionResponse, allocationDefinitionId:number):Observable<AllocationDefinition|undefined>{
        if(!r.success){
            this.handleError(typeof(r), r.message);
            return of(undefined);
        }
        //only expecting max one definition
        const dto = r.allocationDefinitions?.[0];
        if(!dto) return of(undefined);
        return this.processAllocationDefinitionDTO(dto).pipe(
            tap(ad=>{ this.allocationDefinitionByIdCache[allocationDefinitionId]=ad; })
        );
    }

    public createAllocationDefinition(ad:AllocationDefinition){
        return this.fdApiService.createAllocationDefinition(this.authService.currentUserValue!.id, this.authService.entityContextId,this.authService.entityContextType, ad).pipe(
            switchMap((r:AllocationDefinitionResponse)=>{
                if(!r.success) return of(r);
                return this.processCreateAllocationDefinition(r).pipe(map(()=>r));
            }),
            share()
        );
    }

    private processCreateAllocationDefinition(r:AllocationDefinitionResponse):Observable<AllocationDefinition|null>{
        const dto = r.allocationDefinitions?.[0];
        if(!dto){
            this.handleError(typeof(r), r.message);
            return of(null);
        }
        return this.processAllocationDefinitionDTO(dto).pipe(
            tap(c=>{ this.allocationDefinitionsCache?.push(c); })
        );
    }

    public updateAllocationDefinition(ad:AllocationDefinition){
        return this.fdApiService.updateAllocationDefinition(this.authService.currentUserValue!.id, this.authService.entityContextId,this.authService.entityContextType, ad).pipe(
            switchMap((r:AllocationDefinitionResponse)=>{
                if(!r.success) return of(r);
                return this.processUpdateAllocationDefinition(r).pipe(map(()=>r));
            }),
            share()
        );
    }
    private processUpdateAllocationDefinition(r:AllocationDefinitionResponse):Observable<AllocationDefinition|null>{
        const dto = r.allocationDefinitions?.[0];
        if(!dto){
            this.handleError(typeof(r), r.message);
            return of(null);
        }
        return this.processAllocationDefinitionDTO(dto).pipe(
            tap(c=>{
                const cache = this.allocationDefinitionsCache;
                if(cache){
                    const index = cache.findIndex(cd=>cd.id==c.id);
                    if(index>-1) cache[index]=c;
                    else cache.push(c);
                }
                this.allocationDefinitionByIdCache[c.id]=c;
            })
        );
    }

    public deleteAllocationDefinition(ad:AllocationDefinition){
        return this.fdApiService.deleteAllocationDefinition(this.authService.currentUserValue!.id, this.authService.entityContextId,this.authService.entityContextType, ad.id).pipe(map((r:BasicResponse)=>{
            if(r.success) this.processDeleteAllocationDefinition(ad);
            return r;
        }), share());
    }
    private processDeleteAllocationDefinition(ad:AllocationDefinition){
        ad.deleted=true;
    }
    public undeleteAllocationDefinition(ad:AllocationDefinition){
        return this.fdApiService.undeleteAllocationDefinition(this.authService.currentUserValue!.id, this.authService.entityContextId,this.authService.entityContextType, ad.id).pipe(map((r:BasicResponse)=>{
            if(r.success) this.processUndeleteAllocationDefinition(ad);
            return r;
        }), share());
    }
    private processUndeleteAllocationDefinition(ad:AllocationDefinition){
        ad.deleted=false;
    }

    public publishAllocationDefinition(ad:AllocationDefinition, published:boolean){
        return this.fdApiService.publishAllocationDefinition(this.authService.currentUserValue!.id, this.authService.entityContextId,this.authService.entityContextType, ad.id, published).pipe(map((r:BasicResponse)=>{
            return this.processPublishAllocationDefinition(r, ad, published);
        }), share());
    }

    private processPublishAllocationDefinition(r:BasicResponse, ad:AllocationDefinition, published:boolean){
        if(r.success) ad.published=published;
        return r;
    }

    public previewAllocation(file:File, allocationDefinitionId:number){
        return this.fdApiService.previewAllocation(file, allocationDefinitionId);
    }
    //#endregion

    //#region meta data handling
    public getMetaDataRuleDefinitions(forceReload:boolean){
        if(forceReload) this.metaDataRuleDefinitionsCache=undefined;
        if(!this.authorized) return of([]);
        // Data available
        if (this.metaDataRuleDefinitionsCache) return of(this.metaDataRuleDefinitionsCache);
        // Request pending
        else if (this.metaDataRuleDefinitionsObservableCache) return this.metaDataRuleDefinitionsObservableCache;
        // New request needed
        else this.metaDataRuleDefinitionsObservableCache = this.fetchMetaDataRuleDefinitions();

        return this.metaDataRuleDefinitionsObservableCache;
    }

    private fetchMetaDataRuleDefinitions(){
        return this.fdApiService.getMetaDataRuleDefinitions().pipe(map((r)=>this.processMetaDataRuleDefinitions(r)), share());
    }

    private processMetaDataRuleDefinitions(r:MetaDataRuleDefinitionResponse){
        this.metaDataRuleDefinitionsObservableCache=undefined;
        this.metaDataRuleDefinitionsCache=new Array<MetaDataRuleDefinition>(0);
        if(r.success){
            r.metaDataRuleDefinitions?.forEach(dto=>{
                var m = MetaDataRuleDefinition.mapFromDTO(dto)
                this.metaDataRuleDefinitionsCache!.push(m);
            });
        }
        else{
            this.handleError(typeof(r), r.message);
        }
        return this.metaDataRuleDefinitionsCache;
    }

    public getMetaDataRuleDefinitionById(id:number){
        return this.getMetaDataRuleDefinitions(false).pipe(map((r)=>r!.find(mdrd=>mdrd.id==id)));
    }
    //#endregion

    //#region wizard methods
    public wizardPreview(file:File, fileDefinition:FileDefinition, filterDefinition:FilterDefinition | undefined, transformationDefinition:TransformationDefinition | undefined){
        return this.fdApiService.wizardPreview(file, fileDefinition, filterDefinition,transformationDefinition);
    }
    //#endregion

    private handleError(source: string, message: string, navigateToError: boolean = false) {
        const stack = new Error().stack;
        const timestamp = new Date().toISOString();
        console.error(`[${timestamp}]`,`[handleError] source=${source} message=${message}`, stack);
        if (navigateToError) {
            this.router.navigate(['/error']);
        }
    }
}